import React, { useEffect, useState } from 'react';
import { Stack, Text, Box, Card, TextInput, Button, PinInput, Group } from '@mantine/core';
import { Phone, ArrowRight, Shield, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
interface LoginProps {
  onLoginSuccess: () => void;
}
type Step = 'phone' | 'otp' | 'success';
export function Login({
  onLoginSuccess
}: LoginProps) {
  const {
    t,
    isRTL
  } = useLanguage();
  const [step, setStep] = useState<Step>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);
  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 12)}`;
  };
  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setPhoneNumber(formatted);
    setError('');
  };
  const handleSendOtp = async () => {
    if (phoneNumber.replace(/\s/g, '').length < 9) {
      setError('Please enter a valid phone number');
      return;
    }
    setIsLoading(true);
    setError('');
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsLoading(false);
    setStep('otp');
    setResendTimer(60);
  };
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError(t('auth.invalidOtp'));
      return;
    }
    setIsLoading(true);
    setError('');
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsLoading(false);
    setStep('success');
    // Navigate to dashboard after success animation
    setTimeout(() => {
      onLoginSuccess();
    }, 1500);
  };
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    setResendTimer(60);
    setOtp('');
  };
  return <Box style={{
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(135deg, #1a2332 0%, #2d3a4f 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20
  }} data-id="element-346">
      {/* Language Switcher */}
      <Box style={{
      position: 'absolute',
      top: 20,
      right: isRTL ? 'auto' : 20,
      left: isRTL ? 20 : 'auto'
    }} data-id="element-347">
        <LanguageSwitcher data-id="element-348" />
      </Box>

      {/* Logo/Title */}
      <Stack align="center" gap="xs" mb="xl" data-id="element-349">
        <Box p="lg" style={{
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderRadius: '50%'
      }} data-id="element-350">
          <Shield size={40} color="#10b981" data-id="element-351" />
        </Box>
        <Text size="sm" c="rgba(255,255,255,0.7)" ta="center" data-id="element-352">
          {t('auth.welcomeTitle')}
        </Text>
        <Text fw={700} size="xl" c="white" ta="center" data-id="element-353">
          {t('auth.appName')}
        </Text>
      </Stack>

      {/* Login Card */}
      <Card shadow="xl" padding="xl" radius="lg" style={{
      width: '100%',
      maxWidth: 380,
      backgroundColor: '#ffffff'
    }} data-id="element-354">
        {step === 'phone' && <Stack gap="lg" data-id="element-355">
            <Stack gap="xs" align="center" data-id="element-356">
              <Text fw={700} size="lg" c="#1a2332" ta="center" data-id="element-357">
                {t('auth.login')}
              </Text>
              <Text size="sm" c="#64748b" ta="center" data-id="element-358">
                {t('auth.welcomeSubtitle')}
              </Text>
            </Stack>

            <TextInput label={t('auth.phoneNumber')} placeholder={t('auth.phonePlaceholder')} value={phoneNumber} onChange={e => handlePhoneChange(e.target.value)} leftSection={<Phone size={18} color="#64748b" data-id="element-360" />} size="md" radius="md" error={error} styles={{
          input: {
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            direction: 'ltr',
            textAlign: 'left',
            '&:focus': {
              borderColor: '#10b981'
            }
          }
        }} data-id="element-359" />

            <Button fullWidth size="md" radius="md" rightSection={<ArrowRight size={18} data-id="element-362" />} loading={isLoading} onClick={handleSendOtp} styles={{
          root: {
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669'
            }
          }
        }} data-id="element-361">
              {t('auth.sendOtp')}
            </Button>
          </Stack>}

        {step === 'otp' && <Stack gap="lg" data-id="element-363">
            <Stack gap="xs" align="center" data-id="element-364">
              <Text fw={700} size="lg" c="#1a2332" ta="center" data-id="element-365">
                {t('auth.enterOtp')}
              </Text>
              <Text size="sm" c="#64748b" ta="center" data-id="element-366">
                {t('auth.otpSent')}
              </Text>
              <Text size="sm" c="#1a2332" fw={500} style={{
            direction: 'ltr'
          }} data-id="element-367">
                +{phoneNumber}
              </Text>
            </Stack>

            <Box data-id="element-368">
              <PinInput length={6} value={otp} onChange={value => {
            setOtp(value);
            setError('');
          }} type="number" size="lg" radius="md" error={!!error} styles={{
            input: {
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              '&:focus': {
                borderColor: '#10b981'
              }
            }
          }} data-id="element-369" />
              {error && <Text size="xs" c="red" mt="xs" ta="center" data-id="element-370">
                  {error}
                </Text>}
            </Box>

            <Button fullWidth size="md" radius="md" loading={isLoading} onClick={handleVerifyOtp} disabled={otp.length !== 6} styles={{
          root: {
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669'
            },
            '&:disabled': {
              backgroundColor: '#d1d5db'
            }
          }
        }} data-id="element-371">
              {isLoading ? t('auth.verifying') : t('auth.verify')}
            </Button>

            <Group justify="center" data-id="element-372">
              {resendTimer > 0 ? <Text size="sm" c="#64748b" data-id="element-373">
                  {t('auth.resendIn')} {resendTimer} {t('auth.seconds')}
                </Text> : <Button variant="subtle" size="sm" onClick={handleResendOtp} loading={isLoading} data-id="element-374">
                  {t('auth.resendOtp')}
                </Button>}
            </Group>

            <Button variant="subtle" color="gray" size="sm" onClick={() => {
          setStep('phone');
          setOtp('');
          setError('');
        }} data-id="element-375">
              {t('common.back')}
            </Button>
          </Stack>}

        {step === 'success' && <Stack align="center" gap="md" py="xl" data-id="element-376">
            <Box p="lg" style={{
          backgroundColor: '#d1fae5',
          borderRadius: '50%'
        }} data-id="element-377">
              <CheckCircle size={48} color="#10b981" data-id="element-378" />
            </Box>
            <Text fw={700} size="lg" c="#1a2332" ta="center" data-id="element-379">
              {t('common.success')}!
            </Text>
            <Text size="sm" c="#64748b" ta="center" data-id="element-380">
              {t('common.loading')}
            </Text>
          </Stack>}
      </Card>

      {/* Footer */}
      <Text size="xs" c="rgba(255,255,255,0.5)" mt="xl" ta="center" data-id="element-381">
        © 2024 Real Estate Portal. All rights reserved.
      </Text>
    </Box>;
}